import { useEffect, useMemo, useRef } from 'react'
import { useAgentStore } from '@/context/AgentProvider'
import { IconButton } from '@/components/ui/IconButton'
import { useDeleteChat } from '@/hooks/useDeleteChat'
import { Tooltip } from '@/components/ui/Tooltip'
import { useTranslations } from '@/hooks/useTranslations'
import { AgentChat, AgentSessionKind } from '@multiplayer-app/ai-agent-types'
import React from 'react'

export interface ChatHistoryPaginationProps {
  fetchNextPage: () => void
  hasNextPage: boolean | undefined
  isFetchingNextPage: boolean
}

interface ChatHistoryProps {
  fetchNextPage?: ChatHistoryPaginationProps['fetchNextPage']
  hasNextPage?: ChatHistoryPaginationProps['hasNextPage']
  isFetchingNextPage?: ChatHistoryPaginationProps['isFetchingNextPage']
  itemRenderer?: (chat: AgentChat) => React.ReactElement
}

export const ChatHistory = ({
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  itemRenderer
}: ChatHistoryProps = {}) => {
  const t = useTranslations()
  const { deleteChat, isDeleting } = useDeleteChat()
  const chats = useAgentStore((state) => state.chats)
  const activeChatId = useAgentStore((state) => state.activeChatId)
  const setActiveChat = useAgentStore((state) => state.setActiveChat)
  const listRef = useRef<HTMLUListElement>(null)
  const sentinelRef = useRef<HTMLDivElement>(null)

  const items = useMemo(
    () =>
      Object.values(chats)
        // Subagent chats are an internal artifact of a parent chat and must
        // not appear in the history list. The server still emits their
        // updates so other components (e.g. `SubagentChatPreview`) can react
        // — we only hide them here at the list-view boundary.
        .filter((chat) => chat.sessionKind !== AgentSessionKind.SUBAGENT)
        .sort((a, b) => {
          if (a.updatedAt === b.updatedAt) return 0
          return a.updatedAt < b.updatedAt ? 1 : -1
        }),
    [chats]
  )

  useEffect(() => {
    const list = listRef.current
    const sentinel = sentinelRef.current
    if (!list || !sentinel || !fetchNextPage || !hasNextPage) return

    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          void fetchNextPage()
        }
      },
      { root: list, rootMargin: '100px', threshold: 0 }
    )
    obs.observe(sentinel)
    return () => obs.disconnect()
  }, [fetchNextPage, hasNextPage, isFetchingNextPage])

  return (
    <section className='mp-agent-chat-history'>
      <h4>{t.chats}</h4>
      {items.length === 0 ? (
        <div className='mp-agent-chat-history-empty'>{t.noChatsYet}</div>
      ) : (
        <ul ref={listRef} className='mp-agent-chat-history-list'>
          {items.map((chat) => {
            const isActive = chat.id === activeChatId
            return itemRenderer ? (
              <li
                key={chat.id}
                onClick={() => setActiveChat(chat.id)}
                className={`mp-agent-chat-history-item${isActive ? ' active' : ''}`}
              >
                {itemRenderer(chat)}
              </li>
            ) : (
              <li
                key={chat.id}
                onClick={() => setActiveChat(chat.id)}
                className={`mp-agent-chat-history-item${isActive ? ' active' : ''}`}
              >
                <div className='mp-agent-chat-history-item-content'>
                  <span className='mp-agent-chat-history-item-title'>{chat.title || t.untitledChat}</span>
                  <span className='mp-agent-chat-history-item-meta'>
                    {new Date(chat.updatedAt).toLocaleString(undefined, {
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>
                <div className='mp-agent-chat-history-item-actions'>
                  <Tooltip content={t.deleteChat}>
                    <IconButton
                      size='sm'
                      icon='Archive'
                      aria-label={t.deleteChat}
                      disabled={isDeleting(chat.id)}
                      onClick={(e) => {
                        e.stopPropagation()
                        void deleteChat(chat.id)
                      }}
                    />
                  </Tooltip>
                </div>
              </li>
            )
          })}
          {hasNextPage && (
            <li className='mp-agent-chat-history-sentinel' aria-hidden>
              <div ref={sentinelRef} className='mp-agent-chat-history-sentinel-inner' />
              {isFetchingNextPage && <div className='mp-agent-chat-history-loading'>{t.fetchingChats}</div>}
            </li>
          )}
        </ul>
      )}
    </section>
  )
}

export default ChatHistory
